import { authMapper } from '../mappers/auth.mapper';
import { userMapper } from '../mappers/user.mapper';
import { User, UserCreationExtend } from '../models/user';
import { UserSecret } from '../models/user-secret';
import { userSecretStorageService } from '../ui-services/user-secret-storage.service';
import { appUrlsConfig } from './app-urls.config';
import { axiosService } from './axios.service';
/**
 * Login.
 * @param {object} data Data.
 * @param {string} data.username User name.
 * @param {string} data.password Password.
 * @returns {Promise<UserSecret>}
 */
async function login({ username, password }) {
  const response = await axiosService.post(appUrlsConfig.auth.login, { username, password });
  const secret = authMapper.mapSecretFromDto(response.data);
  userSecretStorageService.saveSecret(secret.userSecret);

  return secret;
}

/**
 * SME sign-in at the API: the password alone sends a code and returns no token; the token comes with the code,
 * or at once for a remembered browser.
 * @param {object} data Data.
 * @param {string} data.username User name.
 * @param {string} data.password Password.
 * @param {string} [data.code] The code that was sent.
 * @returns {Promise<{secret: UserSecret|null, challenge: object|null}>}
 */
async function smeSignIn({ username, password, code }) {
  const response = await axiosService.post(appUrlsConfig.auth.login, code ? { username, password, code } : { username, password });

  if (!response.data?.api_token) {
    return { secret: null, challenge: response.data };
  }

  const secret = authMapper.mapSecretFromDto(response.data);
  userSecretStorageService.saveSecret(secret.userSecret);

  return { secret, challenge: null };
}

/** Logout. */
async function logout() {
  await axiosService.get('/oldapi/loginControlInternal.php?command=logout');

  // Use UI logic here to simplify logout logic.
  userSecretStorageService.removeSecret();
}

/**
 * Get current user profile.
 * @returns {Promise<User>}
 */
async function getProfile() {
  const response = await axiosService.get(appUrlsConfig.user.profile);
  return userMapper.fromDto(response.data);
}

/**
 * Create account.
 * @param {UserCreationExtend} data Data.
 * @returns {Promise<User>}
 */
async function createAccount(data) {
  const dataToDto = userMapper.toCreationExtendDto(data);
  const response = await axiosService.post(appUrlsConfig.user.base, dataToDto);
  return userMapper.fromDto(response.data);
}

export const authService = {
  login,
  smeSignIn,
  getProfile,
  logout,
  createAccount,
};
